import { action, atom } from '@reatom/framework'

import { Result } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import { Error } from '@root/shared'
import { ApiError, apiRequest } from '@root/shared/api'
import { tradeApiClientAtom } from '@root/shared/api/trade-api-client-atom'

import type { DelugeId } from '@root/entities/user-short'

/**
 * Bumped whenever this account follows or unfollows somebody.
 *
 * A follower list is a server answer, and a tap on the button changes what
 * that answer would be. Anything showing one spies this and reads again, so
 * unfollowing removes the row there and then instead of leaving it sitting
 * with a Follow button until the screen is reloaded.
 */
export const followsRevisionAtom = atom(0, 'followsRevisionAtom')

export const makeActivityRequestAction = action(
  async (
    ctx,
    options: {
      targetDelugeId: DelugeId
      actionType: 'follow' | 'unfollow'
    },
  ): Promise<Result.Result<string, Error.UnknownError>> => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)
    const currentUser = ctx.get(userAtom)

    if (!currentUser) {
      return Result.fail(Error.UnknownError.fromUnknown())
    }

    const result = await ctx.schedule(() =>
      // Positional, because the route carries no query parameters and the
      // generator writes those as plain arguments.
      apiRequest(ctx, tradeApiClient.user.activityActionCreate, [
        currentUser.delugeId,
        options.targetDelugeId,
        options.actionType,
      ]),
    )

    if (Result.isSuccess(result)) {
      followsRevisionAtom(ctx, (revision) => revision + 1)
    }

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'makeActivityRequestAction',
)
